import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { RestaurantLayout } from "@/components/tabdine/RestaurantLayout";
import { Gift, TrendingUp, Star } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const KPI = ({ label, value, hint, testid }) => (
  <div className="bg-white rounded-xl p-4 border border-brand-dark/5" data-testid={testid}>
    <div className="text-xs uppercase tracking-widest text-brand-dark/50">{label}</div>
    <div className="font-heading text-2xl font-bold text-brand-dark mt-1">{value}</div>
    {hint && <div className="text-xs text-brand-dark/50 mt-0.5">{hint}</div>}
  </div>
);

export default function Dashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const load = () => {
      api.get("/restaurant/dashboard").then((r) => setData(r.data)).catch(() => {});
      api.get("/restaurant/orders").then((r) => setOrders(r.data.slice(0, 5))).catch(() => {});
    };
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  return (
    <RestaurantLayout>
      <div className="p-4 lg:p-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-brand-dark">Dashboard</h1>
            <div className="text-sm text-brand-dark/60">Live overview of today's business</div>
          </div>
          <button
            onClick={() => nav("/restaurant/offers")}
            data-testid="run-offer-btn"
            className="relative inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orange-dark text-white px-4 py-2.5 rounded-full font-medium shadow-sm"
          >
            <Gift size={16} /> Run an Offer
            {data?.active_offers > 0 && (
              <span className="ml-1 inline-flex items-center gap-1 text-xs bg-white/20 rounded-full px-2 py-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400" /> {data.active_offers} live
              </span>
            )}
          </button>
        </div>

        {data ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              <KPI label="Today Revenue" value={inr(data.today_revenue)} testid="kpi-revenue" />
              <KPI label="Today Orders" value={data.today_orders} testid="kpi-orders" />
              <KPI label="Pending" value={data.pending_orders} testid="kpi-pending" />
              <KPI label="Avg Order Value" value={inr(data.avg_order_value)} testid="kpi-aov" />
              <KPI label="NFC Taps" value={data.nfc_taps_today} testid="kpi-taps" />
              <KPI label="New Customers" value={data.new_customers} testid="kpi-newc" />
              <KPI label="Avg Rating" value={`${data.avg_rating || 0} ★`} hint={`${data.total_reviews} reviews`} testid="kpi-rating" />
              <KPI label="Active Offers" value={data.active_offers} testid="kpi-offers" />
            </div>

            <div className="mt-6 grid lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 bg-white rounded-xl p-4 border border-brand-dark/5">
                <div className="flex items-center gap-2 text-sm font-heading font-semibold mb-3">
                  <TrendingUp size={16} className="text-brand-orange" /> Revenue — Last 7 days
                </div>
                <div className="h-56">
                  <ResponsiveContainer>
                    <BarChart data={data.revenue_chart}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="date" fontSize={11} />
                      <YAxis fontSize={11} />
                      <Tooltip />
                      <Bar dataKey="revenue" fill="#C84B11" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 border border-brand-dark/5">
                <div className="text-sm font-heading font-semibold mb-2">Recent orders</div>
                {orders.length === 0 ? (
                  <div className="text-xs text-brand-dark/50 py-6 text-center">No orders yet today.</div>
                ) : (
                  <div className="space-y-2">
                    {orders.map((o) => (
                      <div key={o.id} className="flex items-center justify-between text-sm">
                        <div>
                          <div className="font-medium text-brand-dark">
                            {o.order_no} · {o.table_number ? `T${o.table_number}` : "Takeaway"}
                          </div>
                          <div className="text-xs text-brand-dark/50">{o.items.length} items · {o.status}</div>
                        </div>
                        <div className="font-heading font-semibold">{inr(o.total_amount)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="h-40 grid place-items-center text-brand-dark/50">Loading…</div>
        )}
      </div>
    </RestaurantLayout>
  );
}
